import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {Linear, TimelineLite, TweenLite} from "gsap";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {BonusBunniesGameConfig} from "../BonusBunniesGameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";

export class BonusBunniesZeppelinView extends BaseView {

    private _layer:ScalableLayer;
    private _zeppelinBaseGame:PIXI.spine.Spine;
    private _zeppelinBonusGame:PIXI.spine.Spine;
    private _zeppelinContainer:PIXI.Container;
    private _zeppelinAnimation:TimelineLite;
    private _orientation:string;
    private _startPositionX:number;
    private _endPositionX:number;
    private _screenWidth:number;
    private _zeppelinScaleX:number;
    private _leftToRight:boolean;
    private _isBaseGame:boolean;
    private _zeppelinScale:number;
    private _zeppelinSpeed:number;
    private _startDelay:number;
    private _callBack:Function;
    private _zeppelinWidth:number;
    private _zeppelinBaseGamewidth:number;
    private _zeppelinBonusGamewidth:number;
    private _activated:boolean = false;
    private _onUpdateCounter:number = 30;

    constructor() {
        super();
        this._layer = StageManager.getLayer(BonusBunniesGameConfig.instance.LAYERS.BACKGROUND_ANIMATIONS.name);
        this._layer.addChild(this);
    }

    protected initAnimations():void {
        this._zeppelinContainer = new PIXI.Container();
        this._zeppelinContainer.position.x = -500;
        this.addChild(this._zeppelinContainer);

        this._zeppelinBaseGame = new PIXI.spine.Spine(GameResources.getSpineAsset("ZeppelinBaseGame"));
        this._zeppelinBaseGamewidth = this._zeppelinBaseGame.width;
        this._zeppelinBaseGame.position.y = -100;
        this._zeppelinBaseGame.state.setAnimation(0, "base", true);
        this._zeppelinBaseGame.visible = false;
        this._zeppelinContainer.addChild(this._zeppelinBaseGame);

        this._zeppelinBonusGame = new PIXI.spine.Spine(GameResources.getSpineAsset("ZeppelinBonusGame"));
        this._zeppelinBonusGamewidth = this._zeppelinBonusGame.width;
        this._zeppelinBonusGame.position.y = -100;
        this._zeppelinBonusGame.state.setAnimation(0, "base", true);
        this._zeppelinBonusGame.visible = false;
        this._zeppelinContainer.addChild(this._zeppelinBonusGame);
    }

    protected onResize(resizeData:IResizeData):void {
        this._orientation = resizeData.orientation;
        if(this._orientation == Orientation.LANDSCAPE) {
            this._zeppelinContainer.visible = false;
        } else {
            this._zeppelinContainer.visible = true;
            this._screenWidth = resizeData.width;
        }
    }

    public abort():void {
        if(this._zeppelinAnimation && this._zeppelinAnimation.isActive()) {
            this._zeppelinAnimation.pause();
            this._zeppelinAnimation.kill();
        }
        this._zeppelinBaseGame.visible = false;
        this._zeppelinBonusGame.visible = false;
    }

    public playZeppelinAnimation(baseGame:boolean, forcedStart:boolean):void {
        if(this._zeppelinAnimation && this._zeppelinAnimation.isActive()) {
            this.abort();
        }
        this._leftToRight = true;
        this._isBaseGame = baseGame;
        this._zeppelinScale = MathHelper.randomNumberInRange(1, 0.6, 2);
        if(MathHelper.randomIntInRange(0, 1) > 0) {
            this._leftToRight = false;
        }
        if(forcedStart == true) {
            this._startDelay = 1.5;
        } else {
            this._startDelay = MathHelper.randomIntInRange(5, 15);
        }
        if(this._isBaseGame == true) {
            this._zeppelinWidth = (this._zeppelinBaseGamewidth * this._zeppelinScale);
        } else {
            this._zeppelinWidth = (this._zeppelinBonusGamewidth * this._zeppelinScale);
        }

        this._zeppelinContainer.scale.set(this._zeppelinScale, this._zeppelinScale);
        this._zeppelinContainer.position.x = this.startPositionX;

        this._zeppelinAnimation = new TimelineLite({delay : this._startDelay, onUpdate : () => this.onUpdate()});
        this._zeppelinAnimation.add(() => this.activateZeppelin());
        this._zeppelinAnimation.add(new TweenLite(this._zeppelinContainer.position, this.zeppelinSpeed, {
            x : this.endPositionX,
            ease : Linear.easeNone
        }));
        this._zeppelinAnimation.add(() => this._zeppelinBaseGame.visible = false);
        this._zeppelinAnimation.add(() => this._zeppelinBonusGame.visible = false);
        this._zeppelinAnimation.add(() => this._callBack());
    }

    private onUpdate():void {
        if(this._onUpdateCounter == 0) {
            this._onUpdateCounter = 30;
            if(((this._zeppelinContainer.position.x > (this._screenWidth + this._zeppelinContainer.width)) && this._leftToRight == true)
                || (this._zeppelinContainer.position.x < (0 - this._zeppelinContainer.width) && this._leftToRight == false)) {
                this._zeppelinAnimation.pause();
                this._zeppelinAnimation.kill();
                this._callBack();
            }
        }
        this._onUpdateCounter--;
    }

    private activateZeppelin():void {
        if(this._isBaseGame == true) {
            this._zeppelinBaseGame.scale.x = this.zeppelinScaleX;
            this._zeppelinBaseGame.updateTransform();
            this._zeppelinBaseGame.visible = true;
            this._zeppelinBonusGame.visible = false;
        } else {
            this._zeppelinBonusGame.scale.x = this.zeppelinScaleX;
            this._zeppelinBonusGame.updateTransform();
            this._zeppelinBonusGame.visible = true;
            this._zeppelinBaseGame.visible = false;
        }
    }

    get startPositionX():number {
        if(this._leftToRight == true) {
            this._startPositionX = Math.round(0 - this._zeppelinWidth / 2);
        } else {
            this._startPositionX = Math.round(this._screenWidth + (this._zeppelinWidth / 2));
        }
        return this._startPositionX;
    }

    get endPositionX():number {
        if(this._leftToRight == true) {
            this._endPositionX = 2000;
        } else {
            this._endPositionX = -2000;
        }
        return this._endPositionX;
    }

    get zeppelinScaleX():number {
        if(this._leftToRight == true) {
            this._zeppelinScaleX = -1;
        } else {
            this._zeppelinScaleX = 1;
        }
        return this._zeppelinScaleX;
    }

    get zeppelinSpeed():number {
        this._zeppelinSpeed = MathHelper.randomIntInRange(60, 105);
        return this._zeppelinSpeed;
    }

    set callBack(value:Function) {
        this._callBack = value;
    }

    set activated(value:boolean) {
        this._activated = value;
    }
}